import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import uniqWith from 'lodash/uniqWith';
import { EMPTY, merge, Observable, Subject, Subscription } from 'rxjs';
import { getNewFileItem } from '../../../utils';
import { UploadProgressComponent } from '../../components/upload-progress/upload-progress.component';
import { GsmLetDirective } from '../../directives/gsm-let.directive';
import { compareCustomFileModels } from '../../helpers/compare-files.helper';
import { strictDynamicFormControlCustomEvent } from '../../helpers/custom-event.helper';
import { isAcceptedFiletype } from '../../helpers/file-type.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { EVENT_TYPE_OPEN_FILE, EVENT_TYPE_UPLOAD_FILE } from '../shared-models/custom-events.model';
import { FileUploadProgressType, FileUploadProgressUpdate } from '../shared-models/file-upload.model';

import { CustomFileModel, CustomFileUploadModel, toCustomFileModel } from './custom-file-upload.model';

const REQUIRED_VALIDATOR = control => (control.value && control.value.length ? null : {required: true});

@Component({
  selector:    'gsm-custom-file-upload',
  templateUrl: './custom-file-upload.component.html',
  styleUrls:   ['./custom-file-upload.component.scss'],
  standalone:  true,
  imports:     [
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatIcon,
    NgFor,
    GsmLetDirective,
    UploadProgressComponent,
    NgIf,
    MatIconButton,
    AsyncPipe
  ]
})
@UntilDestroy()
export class CustomFileUploadComponent extends CustomDynamicFormControlComponent<CustomFileUploadModel> implements OnInit {
  /**
   * Progress Subjects need to be tracked separate from our model, because they cannot be (JSON) serialized.
   * This static map is exposed to the instance (and by extension the template) via a getter.
   */
  static readonly progressMap = new WeakMap<
    CustomFileModel,
    {
      progress$: Subject<FileUploadProgressUpdate>;
      cancelUpload?: () => void;
    }
  >();

  @ViewChild('fileUpload') fileUpload: ElementRef;

  isDragging = false;

  private _formControl: AbstractControl;
  private _uploadInProgress = false;
  private _uploadsInProgress: Observable<FileUploadProgressUpdate> = EMPTY;
  private _uploadsInProgressSubscription: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  get progressMap() {
    return CustomFileUploadComponent.progressMap;
  }

  get isValid(): boolean {
    return (
      !this.uploadInProgress && this.model?.value?.every(file => !file.error) && (!this._formControl.touched || this._formControl.valid)
    );
  }

  get uploadInProgress() {
    return this._uploadInProgress;
  }

  set uploadInProgress(val) {
    this._uploadInProgress = val;
    this._emitChange();
  }

  protected onModelChange() {
    this._formControl = this.group.get(this.model.id);

    if (!this.model.value) {
      this.model.value = [];
    }

    if (this.model.required) {
      this._formControl.setValidators(REQUIRED_VALIDATOR);
      this._formControl.updateValueAndValidity();
    }

    this._emitChange();
  }

  protected onValueChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this._formControl.markAsTouched();
    this._formControl.updateValueAndValidity();

    this._uploadFiles();

    this._emitChange();
  }

  private _emitChange() {
    const errors = this.isValid ? null : {customFileUpload: true};
    this.group.get(this.model.id)
      .setErrors(errors);
    this.change.emit(this.model.value);
  }

  onFilesAdded() {
    this._addFiles(Array.from(this.fileUpload.nativeElement.files));
    this.fileUpload.nativeElement.value = ''; // clear out files from input field
  }

  private _addFiles(files: File[]) {
    const filteredFiles = files.filter(file => isAcceptedFiletype(file, this.model.accept));
    let fileModels: CustomFileModel[] = [];

    if (this.model.multiple) {
      fileModels = uniqWith([
        ...this.model.value,
        ...filteredFiles.map(toCustomFileModel)
      ], compareCustomFileModels);
    } else if (filteredFiles[0]) {
      fileModels = [toCustomFileModel(filteredFiles[0])];
    }
    this.model.value = fileModels;

    this._uploadFiles();
  }

  private _uploadFiles(): void {
    // * trigger separate upload events for all files,
    // * track uploads using individual Subjects (to enable separate cancel)
    // * and setup progress handlers
    const newUploads: Subject<FileUploadProgressUpdate>[] = [];

    this.model.value.forEach((file, index) => {
      let progress$ = this.progressMap.get(file)?.progress$;
      if (progress$ || file.fileId || file.error) {
        // * already in progress or upload finished
        return;
      }
      progress$ = new Subject<FileUploadProgressUpdate>();
      this.progressMap.set(file, {progress$});

      progress$.pipe(untilDestroyed(this))
        .subscribe(update => this._handleFileProgress(update, file, index));

      newUploads.push(progress$);
      this.customEvent.emit(
        strictDynamicFormControlCustomEvent(EVENT_TYPE_UPLOAD_FILE, {
          detail: {
            fileInstance:     file.fileInstance,
            progressCallback: val => progress$.next(val)
          }
        })
      );
    });

    // * this might be emptied when called from
    // * `_handleFileProgress` -> `onValueChange` -> `_uploadFiles`
    if (!newUploads.length) {
      return;
    }

    // * prevent multiple subscription to the same observable
    if (this._uploadsInProgressSubscription) {
      this._uploadsInProgressSubscription.unsubscribe();
    }

    // * merge with observable from previous upload(s)
    this._uploadsInProgress = merge(this._uploadsInProgress, ...newUploads);

    this._uploadsInProgressSubscription = this._uploadsInProgress.pipe(untilDestroyed(this))
      .subscribe({
        complete: () => (this.uploadInProgress = false)
      });
  }

  private _handleFileProgress(update: FileUploadProgressUpdate, file: CustomFileModel, index: number): void {
    if (!update) {
      throw new Error('progress update unavailable');
    }

    const progress$ = this.progressMap.get(file)?.progress$;
    if (!progress$) {
      throw new Error('progress Subject not available');
    }

    switch (update.type) {
      case FileUploadProgressType.Init: {
        // * lock component model invalid
        this.uploadInProgress = true;

        // * item initialization
        this.progressMap.set(file, {
          progress$,
          cancelUpload: () => {
            update.cancel();
            progress$.complete();
            this.model.value = this.model.value.filter((_, i) => i !== index);
          }
        });
        return;
      }
      case FileUploadProgressType.Complete: {
        // * set model upon completion/error - also completes and removes file.progress$
        const newFile = getNewFileItem(file, update.result);

        if (progress$) {
          progress$.complete();
          this.progressMap.delete(file);
        }

        if (update.progress.error) {
          newFile.error = true;
        }
        console.log(`complete: ${file.originalFileName}`);
        this.model.value = this.model.value.map((val, i) => (i === index ? newFile : val));
      }
    }
  }

  openFileDialog() {
    this.fileUpload.nativeElement.click();
  }

  removeFile(file: CustomFileModel) {
    this.model.value = this.model.value.filter(item => file !== item);
  }

  openFile(file: CustomFileModel) {
    this.customEvent.emit(
      strictDynamicFormControlCustomEvent(EVENT_TYPE_OPEN_FILE, {
        detail: file
      })
    );
  }

  retryUpload(file: CustomFileModel) {
    const subject = this.model?.value?.find(val => compareCustomFileModels(file, val));
    if (!subject) {
      throw new Error(`file not available`);
    }
    delete subject.error;
    this._uploadFiles();
  }

  /* Handling Drag and Drop */

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    if (ev.dataTransfer.files.length > 0) {
      this._addFiles(Array.from(ev.dataTransfer.files));
      this.isDragging = false;
      this.cd.detectChanges();
    }
  }

  onDragover(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    ev.dataTransfer.dropEffect = 'copy';
    this.isDragging = true;
  }

  onDragleave(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragging = false;
  }
}
